import { isEqual } from "lodash-es";
import { useEffect, useRef } from "react";
import { type RouteComponentProps } from "react-router";

/**
 * Keeps a single-job detail route in step with the left-nav JobPicker, for entities whose detail
 * route is pinned to a job by the URL (e.g. `/ScheduleOfValues/:jobId`, `/Submittal/:id/:jobId`).
 * The entity on screen belongs to exactly one job, so when the picker moves off that job there is no
 * equivalent record to show — the page falls back to its list (`listLink`), which honors the new
 * selection.
 *
 * This replaces a route's old `redirectOnJobChangeURL`, which `ListPageLayout` implements as a
 * `window.location.assign` — a full page reload that throws away the SPA. Navigating through history
 * keeps the app mounted and lets any unsaved-changes guard (built on `history.block`) prompt normally
 * instead of the browser's generic unload dialog.
 *
 * The ref guards the initial render, so a deep link whose stored picker state differs from the URL
 * jobId does not trigger a false redirect. Empty `jobIds` is skipped: it is the AllJobs→NoJobs
 * fallback the picker emits transiently while reloading, not a user selection.
 *
 * While All Jobs stays selected, the picker's expanded id list changes whenever a job is created or
 * deleted elsewhere; that is not a selection change, so it does not redirect.
 *
 * `isAllJobsSelected` is read alongside `jobIds` because a builder with a single job expands
 * "All Jobs" to that one id — indistinguishable from picking the job itself by `jobIds` alone.
 */
export const useJobPickerDetailRedirect = (
    jobIds: number[],
    isAllJobsSelected: boolean,
    urlJobId: number,
    history: RouteComponentProps["history"],
    listLink: string,
) => {
    const previousSelectionRef = useRef<{ jobIds: number[]; isAllJobsSelected: boolean }>();

    useEffect(() => {
        const previous = previousSelectionRef.current;
        // Record the selection before deciding, so a stale `previous` can't redirect twice.
        previousSelectionRef.current = { jobIds, isAllJobsSelected };

        if (previous === undefined) {
            return;
        }
        if (isEqual(previous.jobIds, jobIds) && previous.isAllJobsSelected === isAllJobsSelected) {
            return;
        }
        if (jobIds.length === 0) {
            return;
        }
        // All Jobs on both sides: only the expanded id list refreshed (a job was added or removed),
        // the selection itself didn't change.
        if (previous.isAllJobsSelected && isAllJobsSelected) {
            return;
        }
        // The record on screen still belongs to a selected job, so there is nothing to leave.
        if (!isAllJobsSelected && jobIds.length === 1 && jobIds[0] === urlJobId) {
            return;
        }

        history.replace(listLink);
    }, [jobIds, isAllJobsSelected, urlJobId, history, listLink]);
};
